import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  FaUpload, 
  FaTrash, 
  FaLink, 
  FaImage, 
  FaCheckCircle, 
  FaTimesCircle,
  FaSpinner
} from "react-icons/fa";
import "./AdminBannerUpload.css";

const AdminBannerUpload = () => {
  const [image, setImage] = useState(null);
  const [url, setUrl] = useState("");
  const [banners, setBanners] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    fetchBanners();
  }, []);

  useEffect(() => {
    if (!image) {
      setPreview(null);
      return;
    }

    const objectUrl = URL.createObjectURL(image);
    setPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [image]);

  const fetchBanners = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get("https://backend.aubattle.com/api/banner/all");
      setBanners(res.data);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch banners:", err);
      setError("Failed to load banners. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!image) {
      setError("Please select an image");
      return;
    }
    
    if (!url) {
      setError("Please enter a URL");
      return;
    }

    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      setError("Please enter a valid URL (include http:// or https://)");
      return;
    }

    const formData = new FormData();
    formData.append("image", image);
    formData.append("url", url);

    try {
      setUploading(true);
      setError(null);
      await axios.post("https://backend.aubattle.com/api/banner/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      
      setSuccess("Banner uploaded successfully!");
      setImage(null);
      setUrl("");
      setPreview(null);
      fetchBanners();
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error("Upload failed:", err);
      setError(err.response?.data?.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const deleteBanner = async (id) => {
    if (!window.confirm("Are you sure you want to delete this banner?")) return;
    
    try {
      await axios.delete(`https://backend.aubattle.com/api/banner/${id}`);
      setSuccess("Banner deleted successfully!");
      fetchBanners();
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error("Delete failed:", err);
      setError("Failed to delete banner. Please try again.");
    }
  };

  return (
    <div className="admin-banner-container">
      <div className="admin-banner-header">
        <h2><FaImage /> Banner Management</h2>
        <p>Upload and manage promotional banners</p>
      </div>

      <div className="upload-section">
        <div className="upload-card">
          <h3><FaUpload /> Upload New Banner</h3>
          
          {error && (
            <div className="alert error">
              <FaTimesCircle /> {error}
            </div>
          )}
          
          {success && (
            <div className="alert success">
              <FaCheckCircle /> {success}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="banner-image">
                <FaImage /> Banner Image
              </label>
              <div className="file-upload">
                <input 
                  id="banner-image"
                  type="file" 
                  accept="image/*"
                  onChange={(e) => setImage(e.target.files[0])}
                  required
                />
                <div className="file-preview">
                  {preview ? (
                    <img src={preview} alt="Preview" />
                  ) : (
                    <div className="file-placeholder">
                      <FaImage size={24} />
                      <span>Select an image</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="banner-url">
                <FaLink /> Destination URL
              </label>
              <input
                id="banner-url"
                type="url"
                placeholder="https://example.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                required
              />
            </div>

            <button 
              type="submit" 
              className="upload-btn"
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <FaSpinner className="spin" /> Uploading...
                </>
              ) : (
                <>
                  <FaUpload /> Upload Banner
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      <div className="banners-section">
        <h3><FaImage /> Current Banners</h3>
        
        {isLoading ? (
          <div className="loading-state">
            <FaSpinner className="spin" /> Loading banners...
          </div>
        ) : banners.length === 0 ? (
          <div className="empty-state">
            No banners found. Upload one to get started.
          </div>
        ) : (
          <div className="banners-grid">
            {banners.map((banner) => (
              <div className="banner-card" key={banner._id}>
                <div className="banner-image">
                  <img 
                    src={`https://backend.aubattle.com/uploads/${banner.image}`} 
                    alt={`Banner ${banner._id}`} 
                  />
                </div>
                <div className="banner-details">
                  <div className="banner-url">
                    <FaLink /> 
                    <a 
                      href={banner.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      {banner.url.length > 30 
                        ? `${banner.url.substring(0, 30)}...` 
                        : banner.url}
                    </a>
                  </div>
                  <button 
                    onClick={() => deleteBanner(banner._id)}
                    className="delete-btn"
                  >
                    <FaTrash /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBannerUpload;